import { ERROR_MESSAGES } from '@/constants/errorMessages'

import type { PetDetails } from '@/types/petDetails'
import type { PetDetailsRequest } from '@/types/requests/petDetailsRequest'
import type { PetDetailsResponse } from '@/types/responses/petDetailsResponse'
import type { HospitalDetails } from '@/types/hospitalDetails'
import type { Claim } from '@/types/claim'

export const usePetsDetailsStore = defineStore('petsDetails', {
  state: () => ({
    petsDetails: [] as PetDetails[],
    petsClaimsDetails: [] as PetDetails[],
    archivedPets: [] as PetDetails[],
    hasArchivedPets: false
  }),
  getters: {
    getPetById: (state) => (petId: string) => {
      // Check active pets first
      let pet = state.petsDetails.find((petDetails: PetDetails) => petDetails.pet.petId === petId)

      // If not found in active pets, check archived pets
      if (!pet) {
        pet = state.archivedPets.find((petDetails: PetDetails) => petDetails.pet.petId === petId)
      }

      if (!pet) throw new Error(ERROR_MESSAGES.PET_NOT_FOUND)

      return pet
    },
    activePets: (state) => state.petsDetails.filter((pd) => !!pd.isInsured)
  },
  actions: {
    setPetsDetails(petsDetails: PetDetails[]) {
      this.petsDetails = petsDetails
    },
    setPetsSummary(petsDetails: PetDetails[]) {
      this.petsClaimsDetails = petsDetails
    },
    async getPetsSummary() {
      const { policyId } = await useSelectedPolicy()

      const query: PetDetailsRequest = {
        policyId
      }

      const { response } = useApi(`pet/petssummary`, { query })
      const { petsDetails, hasArchivedPets } = (await response) as PetDetailsResponse

      this.petsDetails = petsDetails
      this.hasArchivedPets = hasArchivedPets ?? false

      // Extract claims from all pets and populate the claims store
      const claimsStore = useClaimsStore()
      const allClaims: Claim[] = []
      if (petsDetails) {
        petsDetails.forEach((petDetail: PetDetails) => {
          if (petDetail.claimsList && petDetail.claimsList.length > 0) {
            // Add petName to each claim since it's needed for display
            const claimsWithPetName = petDetail.claimsList.map((claim: Claim) => ({
              ...claim,
              petName: petDetail.pet.name
            }))
            allClaims.push(...claimsWithPetName)
          }
        })
      }
      claimsStore.setClaims(allClaims)

      return petsDetails
    },
    async getPetsDetails() {
      const { policyId } = await useSelectedPolicy()

      const query: PetDetailsRequest = {
        policyId
      }

      const { response } = useApi(`pet/petsdetails`, { query })
      const { petsDetails, hasArchivedPets } = (await response) as PetDetailsResponse

      this.petsClaimsDetails = petsDetails
      this.hasArchivedPets = hasArchivedPets ?? false

      // Extract claims from all pets and populate the claims store
      const claimsStore = useClaimsStore()
      const allClaims: Claim[] = []

      petsDetails.forEach((petDetail: PetDetails) => {
        if (petDetail.claimsList && petDetail.claimsList.length > 0) {
          // Add petName to each claim since it's needed for display
          const claimsWithPetName = petDetail.claimsList.map((claim: Claim) => ({
            ...claim,
            petName: petDetail.pet.name
          }))
          allClaims.push(...claimsWithPetName)
        }
      })

      claimsStore.setSummaryClaims(allClaims)

      return petsDetails
    },
    async getInactivePetsDetails() {
      const { policyId } = await useSelectedPolicy()

      const query: PetDetailsRequest = {
        policyId
      }

      const { response } = useApi(`pet/inactivepetsdetails`, { query })
      const { petsDetails } = (await response) as PetDetailsResponse

      this.archivedPets = petsDetails

      // Extract claims from archived pets and populate the claims store
      const claimsStore = useClaimsStore()
      const archivedClaims: Claim[] = []

      petsDetails.forEach((petDetail: PetDetails) => {
        if (petDetail.claimsList && petDetail.claimsList.length > 0) {
          // Add petName to each claim since it's needed for display
          const claimsWithPetName = petDetail.claimsList.map((claim: Claim) => ({
            ...claim,
            petName: petDetail.pet.name
          }))
          archivedClaims.push(...claimsWithPetName)
        }
      })

      // Merge archived claims with existing claims
      const existingClaims = claimsStore.summaryClaims
      const allClaims = [...existingClaims, ...archivedClaims]
      claimsStore.setSummaryClaims(allClaims)

      return petsDetails
    },
    addVetClinicToPet(payload: HospitalDetails & { petId: string }) {
      const petDetails = this.getPetById(payload.petId)

      const updatedHospitalList = [...(petDetails.hospitalList || []), payload]

      petDetails.hospitalList = updatedHospitalList
    },
    setProfileImage(payload: { imgUrl: string; petId: string }) {
      const details = this.petsDetails.find((details) => details.pet.petId === payload.petId)
      if (details) {
        details.petProfile.profileImageUrl = payload.imgUrl
      }
    }
  }
})
